/**
 * R53.27 — Scanner QR lotto (apre tracciabilità completa)
 *
 * Pagina mobile-first che apre la fotocamera, legge un QR e naviga
 * automaticamente alla scheda di tracciabilità del lotto scansionato.
 *
 * Il QR atteso contiene un URL `/tracciabilita/<prodId>` (assoluto o relativo).
 * Se contiene solo un ID UUID-like, fallback a `/tracciabilita/<id>`.
 *
 * Route: /scansiona-lotto
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { ArrowLeft, ScanLine, Camera, AlertTriangle, Hash } from "lucide-react";
import { toast } from "sonner";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function ScansionaLotto() {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const containerId = "tracc-qr-scanner-container";
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);
  const [manualInput, setManualInput] = useState("");

  useEffect(() => {
    let inst = null;
    let stopped = false;
    (async () => {
      try {
        inst = new Html5Qrcode(containerId, /* verbose */ false);
        scannerRef.current = inst;
        await inst.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            if (stopped) return;
            stopped = true;
            handleDecoded(decoded);
          },
          () => { /* miss frames ignored */ },
        );
        setRunning(true);
      } catch (e) {
        setError(e?.message || "Impossibile aprire la fotocamera");
      }
    })();
    return () => {
      stopped = true;
      try { inst?.stop().then(() => inst?.clear()).catch(() => {}); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDecoded = (text) => {
    try { scannerRef.current?.stop().then(() => scannerRef.current?.clear()).catch(() => {}); } catch { /* noop */ }
    setRunning(false);
    const id = extractProdId(text);
    if (!id) {
      toast.error("QR non riconosciuto");
      setError(`QR letto: "${(text || "").slice(0, 80)}". Non corrisponde a un lotto valido.`);
      return;
    }
    navigate(`/tracciabilita/${id}`);
  };

  const goManual = () => {
    const id = extractProdId(manualInput);
    if (!id) {
      toast.error("Inserisci un ID o URL valido");
      return;
    }
    navigate(`/tracciabilita/${id}`);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="scan-page">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20 p-4 bg-gradient-to-b from-black/70 to-transparent flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="scan-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-widest text-amber-300 font-bold">Tracciabilità lotto</p>
          <p className="text-sm font-bold">Inquadra il QR sull&apos;etichetta</p>
        </div>
        <ScanLine className="w-5 h-5 text-amber-300" />
      </div>

      {/* Scanner */}
      <div id={containerId} className="w-full h-screen" data-testid="scan-camera" />

      {/* Overlay guida */}
      {running && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10">
          <div className="w-[260px] h-[260px] border-4 border-amber-400 rounded-2xl shadow-[0_0_0_2000px_rgba(0,0,0,0.5)]" />
        </div>
      )}

      {/* Errore + input manuale */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-4 bg-gradient-to-t from-black/80 to-transparent space-y-3">
        {error && (
          <div className="rounded-xl bg-rose-900/60 border border-rose-700 text-rose-100 px-3 py-2 text-[12px] flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p className="flex-1">{error}</p>
          </div>
        )}
        <div className="rounded-xl bg-white/10 border border-white/20 p-2.5 backdrop-blur-sm">
          <p className="text-[10px] uppercase tracking-widest text-white/60 font-bold flex items-center gap-1"><Hash className="w-3 h-3" /> Oppure inserisci manualmente</p>
          <div className="flex gap-2 mt-1.5">
            <input
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              placeholder="ID produzione o URL"
              className="flex-1 px-3 py-2 rounded-lg bg-white/15 text-white placeholder-white/40 outline-none text-[12px] font-mono"
              data-testid="scan-manual-input"
            />
            <button onClick={goManual} className="px-3 py-2 rounded-lg bg-amber-400 text-[#13294B] font-bold text-[12px]" data-testid="scan-manual-go">Vai</button>
          </div>
        </div>
        {!running && !error && (
          <div className="flex items-center gap-2 text-white/60 text-[12px] justify-center">
            <Camera className="w-4 h-4 animate-pulse" /> Avvio fotocamera…
          </div>
        )}
      </div>
    </div>
  );
}

function extractProdId(text) {
  if (!text) return null;
  const t = String(text).trim();
  // URL with /tracciabilita/<id>
  const m = t.match(/\/tracciabilita\/([0-9a-f-]+)/i);
  if (m) return m[1];
  // URL with ?id= or ?prod=
  try {
    const u = new URL(t);
    const q = u.searchParams.get("id") || u.searchParams.get("prod");
    if (q && UUID_RE.test(q)) return q;
  } catch { /* not a URL */ }
  // direct UUID
  if (UUID_RE.test(t)) return t;
  return null;
}
